import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, LogIn } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";

function formatError(detail) {
  if (!detail) return "Erro ao fazer login. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/crm");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleDemo = async () => {
    setError("");
    setLoading(true);
    try {
      await login("admin@newsky.jp", "admin123");
      navigate("/crm");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="NewSky" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-white" data-testid="login-title">CRM Login</h1>
          <p className="text-sm text-slate-400 mt-1">Acesse o painel interno</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4" data-testid="login-form">
          {error && (
            <div data-testid="login-error" className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:border-orange-500/50"
              placeholder="seu@email.com"
            />
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Senha</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:border-orange-500/50"
              placeholder="Sua senha"
            />
          </div>

          <button
            type="submit" disabled={loading}
            data-testid="login-submit-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            Entrar
          </button>

          <div className="text-right mt-2">
            <button type="button" onClick={() => setShowReset(true)} className="text-xs text-slate-500 hover:text-orange-400 transition-colors" data-testid="forgot-password-link">
              Esqueci minha senha
            </button>
          </div>
        </form>

        {/* Reset password modal */}
        {showReset && (
          <div className="mt-4 bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="reset-password-info">
            <p className="text-sm font-bold text-white mb-2">Recuperação de Senha</p>
            <p className="text-xs text-slate-400 leading-relaxed mb-3">
              Para redefinir sua senha, entre em contato com o administrador do sistema ou envie uma mensagem para:
            </p>
            <a href="mailto:internet@newsky.jp" className="text-xs font-medium text-orange-400 hover:text-orange-300">internet@newsky.jp</a>
            <p className="text-xs text-slate-500 mt-3">O administrador pode redefinir sua senha na seção Operadores do CRM.</p>
            <button onClick={() => setShowReset(false)} className="mt-3 text-xs text-slate-500 hover:text-white transition-colors">Fechar</button>
          </div>
        )}

        {/* Demo buttons */}
        <div className="mt-4 space-y-3">
          <button
            onClick={handleDemo}
            disabled={loading}
            data-testid="demo-login-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            Demo Admin (CRM)
          </button>
          <button
            onClick={() => { window.location.href = "/portal/login"; }}
            data-testid="demo-client-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-700 transition-colors"
          >
            Demo Cliente (Portal)
          </button>
        </div>

        {/* Back */}
        <div className="text-center mt-6">
          <a href="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
            Voltar ao site
          </a>
        </div>
      </div>
    </div>
  );
}
